import { Info } from '@mui/icons-material';
import { Snackbar, Typography } from '@mui/material';
import {
  getBusinessUsersPortfolio,
  getDocumentsForCollection,
} from 'lib/api/collections';
import { getIntegrationStatus } from 'lib/api/integrations';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { DocumentProfileContext } from 'lib/context/DocumentProfileProvider';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import MenuButton from 'lib/designSystem/MenuButton';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import {
  hasAnyPermissions,
  showAlert,
  validateCompanyTypes,
  validatePermissions,
} from 'lib/functions/utils';
import PublicationForm from 'pages/Debt/components/InvoicePublication/components/PublicationForm';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

import { getCompanyConfig } from 'lib/api/company';
import BatchPortfolio from './components/BatchPortfolio';
import DocumentManagements from './components/DocumentManagements';
import DocumentsIndicators from './components/DocumentsIndicators';
import NewEnrollment from './components/NewEnrollment';
import NewManagement from './components/NewManagement';
import NewMultipleManagement from './components/NewMultipleManagement';
import PaymentNotifications from './components/PaymentNotifications';
import Unenrollment from './components/Unenrollment';

/* This set the id for the Cobranza Online integration */
const COBRANZA_ONLINE_ID = 1;

const CollectionDocuments = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [dataTable, setDataTable] = useState<any[]>([]);
  const [originalData, setOriginalData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [documentsInMemory, setDocumentsInMemory] = useState<Document[]>([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [openPublishModal, setOpenPublishModal] = useState(false);
  const [openModalAutoCollection, setOpenModalAutoCollection] = useState({
    open: false,
    id: 0,
    stakeholderIdentifier: '',
    paymentDate: '',
  });
  const [openModalUnenrollment, setOpenModalUnenrollment] = useState({
    open: false,
    folio: '',
    collectionFlowExecutions: [],
  });
  const [openPaymentNotifications, setOpenPaymentNotifications] = useState<{
    invoiceId: any;
    open: boolean;
  }>({
    invoiceId: 0,
    open: false,
  });
  const [integrationStatus, setIntegrationStatus] = useState(false);
  const [endDate, setEndDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [stakeholderName, setStakeholderName] = useState('');
  const [clientIdentifier, setClientIdentifier] = useState('');
  const [clientName, setClientName] = useState('');
  const [documentType, setDocumentType] = useState('all');
  const [management, setManagement] = useState('all');
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [executiveId, setExecutiveId] = useState('all');
  const [executiveOptions, setExecutiveOptions] = useState<any[]>([]);
  const [fundId, setFundId] = useState('all');
  const [fundOptions, setFundOptions] = useState<any[]>([]);
  const [canHaveSubordinates, setCanHaveSubordinates] = useState(false);
  const [totalWithNoManagements, setTotalWithNoManagements] = useState(0);
  const [openIndicators, setOpenIndicators] = useState(false);
  const [selected, setSelected] = useState<any[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [status, setStatus] = useState('pending');
  const [businessUsersPortfolio, setBusinessUsersPortfolio] = useState<any[]>(
    [],
  );
  const [byStakeholder, setByStakeholder] = useState(false);
  const [newExecutiveOptions, setNewExecutiveOptions] = useState<any[]>([]);
  const [openBatchPortfolio, setOpenBatchPortfolio] = useState(false);
  const [folio, setFolio] = useState('');
  const [documentsForMultiple, setDocumentsForMultiple] = useState<any[]>([]);
  const [openCreateNewManagementMultiple, setOpenCreateNewManagementMultiple] =
    useState(false);
  const [openNewManagement, setOpenNewManagement] = useState(false);
  const [selectedDocumentForEnrollment, setSelectedDocumentForEnrollment] =
    useState<any>(null);

  const { onOpenDocumentProfile } = useContext(DocumentProfileContext);

  const { data: companyOperatesAsFundData } = getCompanyConfig(dispatch, 'system_company_operates_as_fund');
  const companyOperatesAsFund = companyOperatesAsFundData && String(companyOperatesAsFundData) === '1';

  const generateQuery = ({
    page,
    pageSize,
    exportComplete,
  }: {
    page: number;
    pageSize: number;
    exportComplete: boolean;
  }) => {
    let query = '';
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (businessName)
      query += `businessName=${businessName.replace('&', '%26')}&`;
    if (stakeholderIdentifier)
      query += `stakeholderIdentifier=${stakeholderIdentifier}&`;
    if (stakeholderName) query += `stakeholderName=${stakeholderName}&`;
    if (clientIdentifier) query += `clientIdentifier=${clientIdentifier}&`;
    if (clientName) query += `clientName=${clientName}&`;
    if (documentType) query += `documentType=${documentType}&`;
    if (folio) query += `folio=${folio}&`;
    if (executiveId && executiveId !== 'all')
      query += `executiveId=${executiveId}&`;
    if (fundId && fundId !== 'all') query += `fundId=${fundId}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}&`;
    if (exportComplete) query += `exportComplete=true&`;
    if (status) query += `status=${status}&`;
    if (management) query += `managements=${management}&`;
    return query;
  };

  const canShowOpenDetails = () => {
    if (!originalData.length) return;
    if (selected.length === 0 || selected.length > 1) return false;
    const id = selected[0];
    const request = originalData.find((x: any) => x.id === id);
    if (!request) return false;

    return true;
  };

  const canShowAutomaticEnroll = () => {
    if (!originalData.length) return;
    if (!hasAnyPermissions(user, ['collections_new'])) {
      return false;
    }
    if (selected.length === 0 || selected.length > 1) return false;
    const id = selected[0];
    const element = originalData.find((x: any) => x.id === id);
    if (!element) return false;
    if (element.collectionFlowExecutions.length > 0) return false;
    if (!element.paid) return true;

    return false;
  };

  const canShowAutomaticUnEnroll = () => {
    if (!originalData.length) return;
    if (!hasAnyPermissions(user, ['collections_new'])) {
      return false;
    }
    if (selected.length === 0 || selected.length > 1) return false;
    const id = selected[0];
    const element = originalData.find((x: any) => x.id === id);
    if (!element) return false;
    if (element.collectionFlowExecutions.length > 0) return true;
    if (element.paid) return false;
    return false;
  };

  const canShowPublish = () => {
    if (selected.length === 0 || selected.length > 1) return false;
    return integrationStatus;
  };

  const canShowCreateNewManagementMultiple = () => {
    if (selected.length <= 1) return false;
    return true;
  };

  const handleManagement = async () => {
    if (selected.length === 0 || selected.length > 1) return;
    const id = selected[0];
    setSelectedDocumentId(id);
    setOpen(true);
  };

  const handleAutomaticUnEnroll = async () => {
    if (selected.length === 0 || selected.length > 1) return;
    const id = selected[0];
    const document = originalData.find((x: any) => x.id === id);
    if (!document) return false;
    const folio = document.folio;
    const collectionFlowExecutions = document.collectionFlowExecutions;
    if (collectionFlowExecutions.length == 0) {
      return showAlert(
        dispatch,
        'error',
        'Este documento no está enrolado en un flujo de cobranza',
      );
    }
    setOpenModalUnenrollment({
      open: true,
      folio: folio,
      collectionFlowExecutions: collectionFlowExecutions,
    });
  };

  const handlePublish = async () => {
    if (!originalData.length) return;

    if (selected.length === 0 || selected.length > 1) return;
    setOpenPublishModal(true);
  };

  const handleOpenAutoCollectionModal = () => {
    if (!originalData.length) return;

    if (selected.length === 0 || selected.length > 1) return;
    const id = selected[0];
    const document = originalData.find((x: any) => x.id === id);
    if (!document) return false;
    setSelectedDocumentForEnrollment(document);

    setOpenModalAutoCollection({
      open: true,
      id: id,
      stakeholderIdentifier: document.stakeholderIdentifier,
      paymentDate: document.paymentDate,
    });
  };

  const handleOpenPaymentNotifications = () => {
    if (selected.length === 0 || selected.length > 1) return;
    const id = selected[0];
    setOpenPaymentNotifications({ invoiceId: id, open: true });
  };

  const handleOpenIndicators = () => {
    setOpenIndicators(true);
  };

  const handleShowClientDetails = (id: string) => {
    const row: any = dataTable.find((x: any) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.clientIdentifier,
        name: row.clientName,
      }),
    );
  };

  const handleShowBusinessDetails = (id: string) => {
    const row: any = dataTable.find((x: any) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.businessIdentifier,
        name: row.businessName,
      }),
    );
  };

  const handleShowStakeholderDetails = (id: string) => {
    const row: any = dataTable.find((x: any) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.stakeholderIdentifier,
        name: row.stakeholderName,
      }),
    );
  };

  const getCobranzaOnlineStatus = async () => {
    try {
      const response = await getIntegrationStatus(dispatch, COBRANZA_ONLINE_ID);
      setIntegrationStatus(response.status);
    } catch (error) {
      console.error(error);
    }
  };

  const confirmSelectionMultiple = () => {
    if (!selected.length) {
      return showAlert(
        dispatch,
        'error',
        'Debes seleccionar al menos un documento',
      );
    }
    const documentsForMultiple = selected.map((id: string) => {
      const document = documentsInMemory.find((x: any) => x.id === id);
      return document;
    });
    setDocumentsForMultiple(documentsForMultiple);
    setOpenCreateNewManagementMultiple(true);
  };

  const actions = [
    {
      action: handleManagement,
      label: 'Gestiones',
      canShow: canShowOpenDetails,
    },
    {
      action: handleOpenAutoCollectionModal,
      label: 'Enrolar a flujo de cobranza',
      canShow: canShowAutomaticEnroll,
    },
    {
      action: handleAutomaticUnEnroll,
      label: 'Desenrolar de flujo de cobranza',
      canShow: canShowAutomaticUnEnroll,
    },
    {
      action: handleOpenPaymentNotifications,
      label: 'Notificaciones de pago',
      canShow: canShowOpenDetails,
    },
    {
      action: handlePublish,
      label: 'Publicar en cobranza online',
      canShow: canShowPublish,
    },
    {
      action: confirmSelectionMultiple,
      label: 'Crear nueva gestión multiple',
      canShow: canShowCreateNewManagementMultiple,
    },
  ];

  const fetchInvoices = async ({
    page,
    pageSize,
    exportComplete = false,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    try {
      setSelected([]);
      const query = generateQuery({ page, pageSize, exportComplete });
      const response = await getDocumentsForCollection(dispatch, query);
      if (!exportComplete) {
        setDataTable(response.data);
        setOriginalData(response.data);
        const newDocumentsInMemory = [...documentsInMemory, ...response.data];
        setDocumentsInMemory(newDocumentsInMemory);
        setTotalWithNoManagements(response.totalWithNoManagements);
        setPagination(response.pagination);
        setExecutiveOptions(response.executiveOptions);
        setFundOptions(response.fundOptions);
      }
      return response;
    } catch (error) {
      console.error(error);
    }
    setSelected([]);
  };

  const fetchData = async () => {
    try {
      const response = await getBusinessUsersPortfolio(dispatch);
      setBusinessUsersPortfolio(response.data);
      setNewExecutiveOptions(response.executivesOptions);
      setByStakeholder(response.byStakeholder);
    } catch (error) {
      console.error(error);
    }
  };

  const reloadDocuments = async () => {
    try {
      setLoading(true);
      const query = generateQuery({
        page: pagination.page,
        pageSize: pagination.pageSize,
        exportComplete: false,
      });
      setSelected([]);
      const response = await getDocumentsForCollection(dispatch, query);
      setDataTable(response.data);
      setOriginalData(response.data);
      const newDocumentsInMemory = [...response.data];
      setDocumentsInMemory(newDocumentsInMemory);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const setUpData = async () => {
    setLoading(true);
    await fetchInvoices({ page: 0, pageSize: PAGE_SIZE });
    await getCobranzaOnlineStatus();
    const boss = validatePermissions(user, 'have_subordinates');
    const admin = validatePermissions(user, 'edit_subordinates');
    if (admin) await fetchData();
    setCanHaveSubordinates(boss || admin);
    setIsAdmin(admin);
    setLoading(false);
  };

  useEffect(() => {
    void setUpData();
  }, []);

  const businessesWithNoExecutives = businessUsersPortfolio.filter(
    (business: any) => business.users.length === 0,
  );

  const cantSelect = (document: any) => {
    if (selected.length === 0) {
      return false;
    }
    const documentAlreadySelected = selected.find(
      (id: string) => id === document.id,
    );
    if (documentAlreadySelected) {
      return false;
    }

    const anySelectedDocument: any = documentsInMemory.find((x: any) =>
      selected.includes(x.id),
    );

    if (
      document.stakeholderIdentifier ==
      anySelectedDocument?.stakeholderIdentifier
    ) {
      return false;
    }

    return true;
  };

  const triggerSearch = async () => {
    setLoading(true);
    await fetchInvoices({ page: 0, pageSize: PAGE_SIZE });
    setLoading(false);
  };

  const isAccountant = validateCompanyTypes(user, 'Accountant');

  const filters: Filter[] = [
    {
      label: 'Estado',
      type: 'select',
      value: status,
      setter: setStatus,
      options: [
        { label: 'No pagado', value: 'pending' },
        { label: 'Pagado', value: 'completed' },
      ],
    },
    {
      label: 'Tipo de documento',
      type: 'select',
      value: documentType,
      setter: setDocumentType,
      options: [
        { label: 'Todos', value: 'all' },
        ...(isAccountant ? [{ label: 'Factura', value: 'invoice' }] : []),
        { label: 'Solicitud de factoring', value: 'factoring' },
        { label: 'Solicitud de crédito', value: 'credit' },
        { label: 'Solicitud de confirming', value: 'confirming' },
        { label: 'Garantía siniestrada', value: 'warranty-claim' },
      ],
    },
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
    {
      label: 'Fecha de vencimiento',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha de vencimiento',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    ...(companyOperatesAsFund ? [
      {
        label: 'Nombre cliente',
        type: 'autocomplete' as const,
        value: clientName,
        key: 'clientName',
        setter: setClientName,
      },
      {
        label: 'Rut cliente',
        type: 'text' as const,
        value: clientIdentifier,
        setter: setClientIdentifier,
      },
    ] : []),
    {
      label: 'Nombre emisor',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
    {
      label: 'Rut emisor',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      label: 'Nombre deudor',
      type: 'autocomplete',
      value: stakeholderName,
      key: 'stakeholderName',
      setter: setStakeholderName,
    },
    {
      label: 'Rut deudor',
      type: 'text',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    {
      label: 'Fondo',
      type: 'select',
      value: fundId,
      setter: setFundId,
      options: [
        ...fundOptions.map((fund: any) => ({
          label: fund.label,
          value: fund.value,
        })),
        { label: 'Sin fondo', value: 'noFund' },
        { label: 'Con fondo', value: 'withFund' },
        { label: 'Todos', value: 'all' },
      ],
    },
    {
      label: 'Gestión programada',
      type: 'select',
      value: management,
      setter: setManagement,
      options: [
        { label: 'Todos', value: 'all' },
        { label: 'Con gestión programada', value: 'scheduled' },
        { label: 'Sin gestión programada', value: 'free' },
      ],
    },
    ...(canHaveSubordinates
      ? [
        {
          label: 'Ejecutivo cobranza',
          type: 'select',
          value: executiveId,
          setter: setExecutiveId,
          options: [
            { label: 'Todos', value: 'all' },
            ...executiveOptions.map((exec: any) => ({
              label: exec.label,
              value: exec.value,
            })),
          ],
        } as Filter,
      ]
      : []),
  ];

  return (
    <>
      {loading && <CenteredCircularProgress type="layout" />}
      {!loading && (
        <div>
          <Snackbar
            open={snackbar.open}
            onClose={() => setSnackbar({ message: '', open: false })}
            message={snackbar.message}
          />
          <PaymentNotifications
            invoiceId={openPaymentNotifications.invoiceId}
            open={openPaymentNotifications.open}
            setOpen={setOpenPaymentNotifications}
            fetchInvoices={fetchInvoices}
          />
          {openModalAutoCollection.open && (
            <NewEnrollment
              setOpen={setOpenModalAutoCollection}
              id={openModalAutoCollection.id}
              paymentDate={openModalAutoCollection.paymentDate}
              stakeholderIdentifier={
                openModalAutoCollection.stakeholderIdentifier
              }
              open={openModalAutoCollection.open}
              invoiceData={originalData.find(
                (x: any) => x.id === openModalAutoCollection.id,
              )}
              setUp={reloadDocuments}
              setOpenNewManagement={setOpenNewManagement}
            />
          )}
          {openModalUnenrollment.open && (
            <Unenrollment
              setModal={setOpenModalUnenrollment}
              open={openModalUnenrollment.open}
              folio={openModalUnenrollment.folio}
              collectionFlowExecutions={
                openModalUnenrollment.collectionFlowExecutions
              }
              setUp={reloadDocuments}
            />
          )}
          <HeaderWithActions
            variant="body1"
            customTitle={
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Typography>Documentos pendientes de cobro.</Typography>
                <Typography
                  onClick={() => {
                    handleOpenIndicators();
                  }}
                  style={
                    canHaveSubordinates
                      ? { cursor: 'pointer' }
                      : { cursor: 'default' }
                  }
                  color="primary"
                  fontWeight={600}
                >
                  [{totalWithNoManagements}/{pagination.total} sin gestión
                  programada]
                </Typography>
                {isAdmin && businessUsersPortfolio.length > 0 && (
                  <Typography
                    onClick={() => {
                      if (!isAdmin) return;
                      setOpenBatchPortfolio(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    color="primary"
                    fontWeight={600}
                  >
                    [{businessesWithNoExecutives.length}/
                    {businessUsersPortfolio.length}{' '}
                    {byStakeholder ? 'deudores' : 'clientes'} sin ejecutivo de
                    cobranza asignado]
                  </Typography>
                )}
              </div>
            }
            buttons={[
              {
                name: 'Buscar',
                action: () => { },
                color: 'primary',
                customButton: (
                  <GlobalSearcher
                    filters={filters}
                    triggerSearch={triggerSearch}
                    dispatch={dispatch}
                    disableSearch={loading}
                  />
                ),
              },
              {
                name: 'Acciones',
                action: () => { },
                color: 'primary',
                customButton: (
                  <MenuButton
                    label={'Acciones'}
                    color={'primary'}
                    options={actions}
                    disabled={selected.length === 0}
                  />
                ),
              },
            ]}
          />
          <TableWithPagination
            dataHeaders={[
              {
                key: 'autoCollectionStatus',
                label: 'En flujo',
                type: 'auto-collection-status',
              },
              { key: 'documentTypeEsp', label: 'Tipo documento' },

              {
                key: 'invoiceType',
                label: 'Tipo',
                type: 'invoice-type-no-icon',
              },
              {
                key: 'siiDescription',
                label: 'Descripción SII',
              },
              ...(companyOperatesAsFund ? [
                {
                  key: 'clientName',
                  label: 'Cliente',
                  handleAction: handleShowClientDetails,
                  icon: <Info fontSize="small" />,
                },
                {
                  key: 'clientIdentifier',
                  label: 'Rut',
                },
              ] : []),
              {
                key: 'businessName',
                label: 'Emisor',
                handleAction: handleShowBusinessDetails,
                icon: <Info fontSize="small" />,
              },
              {
                key: 'businessIdentifier',
                label: 'Rut',
              },
              {
                key: 'stakeholderName',
                label: 'Receptor',
                handleAction: handleShowStakeholderDetails,
                icon: <Info fontSize="small" />,
              },
              {
                key: 'stakeholderIdentifier',
                label: 'Rut',
              },
              {
                key: 'folio',
                label: 'Folio',
                handleAction: onOpenDocumentProfile,
                icon: <Info fontSize="small" />,
              },
              { key: 'invoiceAmount', label: 'Monto original', type: 'money' },
              {
                key: 'operationDiscountAmount',
                label: 'Descuento operación',
                type: 'money',
              },
              {
                key: 'creditNoteAmount',
                label: 'Monto nota crédito',
                type: 'money',
              },
              { key: 'amount', label: 'Monto', type: 'money' },
              {
                key: 'amountConciliated',
                label: 'Conciliado',
                type: 'money',
              },
              {
                key: 'documentRemainingAmount',
                label: 'Saldo documento',
                type: 'money',
              },
              {
                key: 'documentRemainingAmountNet',
                label: 'Saldo financiado',
                type: 'money',
              },
              {
                key: 'arrearAmount',
                label: 'Mora',
                type: 'money',
              },
              {
                key: 'totalRemainingNet',
                label: 'Saldo financiado + mora',
                type: 'invoice-remaining-amount',
              },
              { key: 'documentState', label: 'Estado documento' },
              { key: 'issuedDate', label: 'Emisión', type: 'date' },
              {
                key: 'receiptDate',
                label: 'Recepción',
                type: 'eight-days-date',
              },
              {
                key: 'originalPaymentDate',
                label: 'Vencimiento original',
                type: 'date',
              },
              { key: 'paymentDate', label: 'Vencimiento', type: 'date' },

              {
                key: 'arrearTrench',
                label: 'Tramo de mora',
                type: 'arrear-trench',
              },
              {
                key: 'arrearDays',
                label: 'Días de mora',
              },
              {
                key: 'possiblePaymentDate',
                label: 'Posible pago',
                type: 'date',
              },
              {
                key: 'nextCollectionManagement',
                label: 'Próxima gestión',
                type: 'management',
                canNull: true,
              },
              { key: 'extensionDate', label: 'Extensión', type: 'date' },
              { key: 'fundName', label: 'Fondo' },
              { key: 'cancelDate', label: 'Rechazo', type: 'date' },
              {
                key: 'confirmDate',
                label: 'Confirmación',
                type: 'date',
              },
              {
                key: 'lastCollectionManagementState',
                label: 'Estado gestión',
              },
              {
                key: 'lastCollectionManagementSubState',
                label: 'Subestado gestión',
              },
              {
                key: 'lastCollectionManagementDescription',
                label: 'Observación gestión',
              },
              { key: 'operationId', label: 'Solicitud', type: 'numeric-id' },
              { key: 'operationDate', label: 'Fecha Solicitud', type: 'date' },
              { key: 'financingAt', label: 'Fecha giro', type: 'date' },
              { key: 'collectorName', label: 'Ejecutivo cobranza' },
              {
                key: 'commercialExecutiveAssignedName',
                label: 'Ejecutivo comercial asignado',
              },
              {
                key: 'commercialExecutivePortfolioName',
                label: 'Ejecutivo comercial cartera',
              },
              {
                key: 'lastConciliationDate',
                label: 'Última conciliación',
                type: 'date',
              },
              {
                key: 'lastConciliationName',
                label: 'Tipo conciliación',
              },
              ...(companyOperatesAsFund ? [
                { key: 'clientTags', label: 'Tags cliente' },
              ] : []),
              { key: 'businessTags', label: 'Tags emisor' },

              { key: 'stakeholderTags', label: 'Tags deudor' },
              {
                key: 'stakeholderSalesSegment',
                label: 'Segmento comercial',
                type: 'sales-segment',
              },
              {
                key: 'stakeholderEconomicSegment',
                label: 'Segmento económico',
              },
              {
                key: 'stakeholderEconomicSubSegment',
                label: 'Subsegmento económico',
              },
              {
                key: 'stakeholderEconomicActivity',
                label: 'Actividad económica',
              },
              { key: 'stakeholderRegion', label: 'Región' },
              { key: 'stakeholderCommune', label: 'Comuna' },
              { key: 'stakeholderCollaborators', label: 'Colaboradores' },
            ]}
            data={dataTable}
            showActions
            rowsPerPageDefault={50}
            exportToFile="xlsx"
            exportComplete
            setData={setDataTable}
            searchData={fetchInvoices}
            pagination={pagination}
            setPagination={setPagination}
            selected={selected}
            setSelected={setSelected}
            cantSelectRow={cantSelect}
            lastElement
            setLoading={setLoading}
          />
          {open && (
            <DocumentManagements
              documentId={selectedDocumentId}
              open={open}
              setOpen={setOpen}
            />
          )}
          {openIndicators && (
            <DocumentsIndicators
              openIndicators={openIndicators}
              setOpenIndicators={setOpenIndicators}
            />
          )}
          {openBatchPortfolio && (
            <BatchPortfolio
              businessesWithNoExecutives={businessesWithNoExecutives}
              byStakeholder={byStakeholder}
              newExecutiveOptions={newExecutiveOptions}
              openBatchPortfolio={openBatchPortfolio}
              setOpenBatchPortfolio={setOpenBatchPortfolio}
              fetchData={fetchData}
            />
          )}
          <PublicationForm
            open={openPublishModal}
            setOpen={setOpenPublishModal}
            documentId={selected[0]}
          />
          {openCreateNewManagementMultiple && (
            <NewMultipleManagement
              openModal={openCreateNewManagementMultiple}
              setOpenModal={setOpenCreateNewManagementMultiple}
              documentsWithId={documentsForMultiple}
              setUp={reloadDocuments}
            />
          )}
          {openNewManagement && (
            <NewManagement
              openModal={openNewManagement}
              setOpenModal={setOpenNewManagement}
              documentId={selectedDocumentForEnrollment.id}
              setUp={reloadDocuments}
              triggerSetUpWhenClose={true}
            />
          )}
        </div>
      )}
    </>
  );
};

export default CollectionDocuments;
